import { useContext } from 'react';

import { RouteResetErrorBoundary } from 'app-level/RouteResetErrorBoundary';
import Header from 'app-level/HeaderNav/Header/Header';
import { getMainRoute, useGetMainPageUrl, NavigationStateProvider } from 'app-level/navigation';
import { FeatureFlagProvider } from 'context/FeatureFlagContext';
import { TourProvider } from 'tours';
import Account from 'features/pages/account/Account';
import { HomePage } from 'features/pages/home/index.page';
import { MarketDataPage } from 'features/pages/market-data/index.page';
import Marketplace from 'features/pages/marketplace/Marketplace';
import Portfolio from 'features/pages/portfolio/Portfolio/Portfolio';
import { PortfolioContextProvider } from 'features/pages/portfolio/PortfolioContext';
import { Toaster } from 'react-hot-toast';
import { Navigate, Route, Routes } from 'react-router-dom';
import BaseWeb from 'style/BaseWeb';
import 'style/main.css';
import 'style/main.scss'; // TODO: Remove once we migrate from SCSS to tailwindcss
import ORSNNTheme, { theme } from 'style/ORSNNTheme';
import { StyledTooltip } from 'ui-kit';
import { normalizeFeatureFlags } from 'utils/normalizeFeatureFlags';

import { useQuery } from '@apollo/client';

import { GET_FEATURE_FLAGS } from 'query/getFeatureFlags';

import { AuthContext, AuthState } from './context/AuthContext';
import { DataContext, DataContextActionType, DataContextProvider } from './context/DataContext';
import UserAuthentication from './features/UserAuthentication';

const App = (): JSX.Element => {
  const { state } = useContext(AuthContext);
  const { dispatch } = useContext(DataContext);

  const isUserSignedIn = [AuthState.SignedIn].includes(state.authState) && state.user != null;

  // Fetch feature flags - works with or without authentication
  // This allows flags to be available even during sign-in flow
  const { data } = useQuery(GET_FEATURE_FLAGS, {
    errorPolicy: 'ignore', // Don't throw errors if query fails (e.g., no auth)
    fetchPolicy: 'cache-first',
    onCompleted: () => {
      if (isUserSignedIn) {
        dispatch({ type: DataContextActionType.SET_LOADING, payload: false });
      }
    },
    onError: () => {
      if (isUserSignedIn) {
        dispatch({ type: DataContextActionType.SET_LOADING, payload: false });
      }
    },
  });

  const getUrl = useGetMainPageUrl();

  const authenticatedApp = (
    <NavigationStateProvider>
      <div className="flex h-full flex-col">
        <Toaster
          toastOptions={{
            className: '',
            style: {
              border: '1px solid #731172',
              borderRadius: '6px',
              padding: '16px',
              color: '#ffffff',
              backgroundColor: '#1f1f1f',
            },
          }}
        />
        <DataContextProvider>
          <Header />
          <div className="page-container grid flex-1 grid-cols-[min-content_12px_1fr_24px] grid-rows-[min-content_1fr] overflow-auto">
            <PortfolioContextProvider>
              <RouteResetErrorBoundary spanPageContainerGrid title="This page couldn't be loaded">
                <Routes>
                  <Route path={getMainRoute('portfolio')} element={<Portfolio />} />
                  <Route path={getMainRoute('marketplace')} element={<Marketplace />} />
                  <Route path={getMainRoute('account')} element={<Account />} />
                  <Route path={'market-data'} element={<MarketDataPage />} />
                  <Route path={getMainRoute('home')} element={<HomePage />} />
                  <Route path="*" element={<Navigate to={getUrl('home')} replace />} />
                </Routes>
              </RouteResetErrorBoundary>
            </PortfolioContextProvider>
          </div>
        </DataContextProvider>
      </div>
    </NavigationStateProvider>
  );

  const featureFlags = data?.featureFlags ? normalizeFeatureFlags(data.featureFlags) : {};

  return (
    <div
      className="m-0 h-full min-h-full text-[13px] font-normal leading-6 tracking-[0.88px] transition-all duration-300"
      style={{
        backgroundColor: theme.color.bgApp,
        color: theme.color.fgDefault,
      }}
    >
      <ORSNNTheme>
        <BaseWeb>
          <FeatureFlagProvider flags={featureFlags}>
            <TourProvider>
              {isUserSignedIn ? authenticatedApp : <UserAuthentication />}
            </TourProvider>
          </FeatureFlagProvider>
          <StyledTooltip id="tooltip" />
        </BaseWeb>
      </ORSNNTheme>
    </div>
  );
};

export default App;
